import React, { useEffect, useState } from 'react';

import {
  useMediaQuery,
  Stack,
  Typography,
  useTheme,
  Box,
  Button,
  TextField,
  Paper,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockIcon from '@mui/icons-material/Lock';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import banner_image from '../../../../assets/banner_image.svg';
import { useTranslation } from 'react-i18next';

export type EnterEmailProps = {
  next: (email: string) => void;
};

function EnterEmail({ next }: EnterEmailProps) {
  const theme = useTheme();
  const aboveSmall = useMediaQuery(theme.breakpoints.up('sm'));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('xs'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

  const [email, setEmail] = useState<string | undefined>();
  const [invalidEmail, setInvalidEmail] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState(false);

  const { t } = useTranslation();

  useEffect(() => {
    // Delay the animation slightly to ensure smooth rendering
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!email) {
      setInvalidEmail(false);
      return;
    }

    const validateEmail = (email: string) => {
      return email.match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
    };

    setInvalidEmail(!validateEmail(email));
  }, [email]);

  const onContinue = async () => {
    if (email) {
      next(email);
    }
  };

  return (
    <Stack
      direction='column'
      alignItems='center'
      sx={{
        minHeight: 'calc(var(--vh) * 100)',
      }}
    >
      <Box
        margin={2}
        marginBottom={0}
        component='img'
        src={banner_image}
        sx={{
          height: aboveSmall ? 36 : isExtraSmall ? 24 : 32,
          filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.1))',
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(-20px)',
          transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
        }}
      />

      <Stack
        flexGrow={1}
        alignItems='center'
        sx={{
          margin: aboveSmall ? '0rem' : '1rem',
          marginTop: aboveSmall ? '6rem' : '3rem',
          maxWidth: '800px',
        }}
      >
        <Box
          sx={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
            transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
            transitionDelay: '0.2s',
            width: '100%',
          }}
        >
          <Typography
            variant='h4'
            component='h4'
            fontWeight={600}
            align='center'
            sx={{
              fontSize: isExtraSmall ? '1.75rem' : aboveSmall ? '3rem' : '2rem',
              marginBottom: '1rem',
              background: 'rgba(0, 0, 0, 0.9)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t('letsCleanYourInbox')}
          </Typography>

          {!isSmall && (
            <Typography
              variant='subtitle1'
              align='center'
              sx={{
                fontSize: isExtraSmall ? '1rem' : '1.2rem',
                color: 'rgba(0, 0, 0, 0.7)',
                maxWidth: '600px',
                marginBottom: '2rem',
                margin: '0 auto',
              }}
            >
              {t('enterEmail.joinThousands')}
            </Typography>
          )}

          <Paper
            elevation={0}
            sx={{
              padding: isSmall ? '2rem 1rem' : '2rem',
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              borderRadius: '24px',
              maxWidth: '500px',
              border: '1px solid rgba(66, 133, 244, 0.1)',
              margin: isSmall ? '2rem auto' : '2rem auto',
            }}
          >
            <TextField
              error={invalidEmail}
              type={'email'}
              helperText={invalidEmail && t('enterValidEmail')}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  fontSize: aboveSmall ? '1.2rem' : isExtraSmall ? '1rem' : '1.1rem',
                  padding: '0.75rem 1.25rem',
                  borderRadius: '50px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  transition: 'all 0.2s ease-in-out',
                  '& fieldset': {
                    borderColor: 'rgba(66, 133, 244, 0.3)',
                    borderWidth: '2px',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(66, 133, 244, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(66, 133, 244, 0.8)',
                  },
                },
              }}
              placeholder={t('enterYourEmail') || undefined}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setEmail(event.target.value);
              }}
              variant='outlined'
              color='primary'
              focused
            />

            <Button
              variant='contained'
              size='large'
              color='googleBlue'
              disabled={!email || invalidEmail}
              sx={{
                width: '100%',
                marginTop: '1rem',
                fontSize: aboveSmall ? '1.2rem' : isExtraSmall ? '1rem' : '1.1rem',
                fontWeight: 700,
                padding: '1rem 2rem',
                borderRadius: '50px',
                textAlign: 'center',
                boxShadow: '0px 8px 16px rgba(66, 133, 244, 0.2)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0px 12px 20px rgba(66, 133, 244, 0.3)',
                },
              }}
              onClick={onContinue}
              endIcon={<ArrowForwardIcon />}
            >
              {t('continue')}
            </Button>

            <Stack direction='row' spacing={3} justifyContent='center' sx={{ marginTop: '2rem' }}>
              <Stack direction='row' spacing={1} alignItems='center'>
                <LockIcon sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '1rem' }} />
                <Typography variant='body2' sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                  {t('enterEmail.secureAndEncrypted')}
                </Typography>
              </Stack>
              <Stack direction='row' spacing={1} alignItems='center'>
                <VerifiedUserIcon sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '1rem' }} />
                <Typography variant='body2' sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                  {t('enterEmail.privacyGuaranteed')}
                </Typography>
              </Stack>
            </Stack>
          </Paper>

          <Stack direction='column' spacing={2} alignItems='center' sx={{ marginTop: '2rem' }}>
            <Typography variant='body2' sx={{ color: 'rgba(0, 0, 0, 0.5)' }}>
              {t('enterEmail.trustedByTeams')}
            </Typography>
            <Stack
              direction='row'
              spacing={4}
              sx={{
                opacity: 0.5,
                filter: 'grayscale(1)',
              }}
            >
              {/* You'll need to add actual company logos here */}
              <Typography variant='body1' sx={{ fontWeight: 600 }}>
                Google
              </Typography>
              <Typography variant='body1' sx={{ fontWeight: 600 }}>
                Microsoft
              </Typography>
              <Typography variant='body1' sx={{ fontWeight: 600 }}>
                Apple
              </Typography>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
}

export default EnterEmail;
